<template>
  <div class="page page-breakdown-results">
    <!-- OVERLAY FOR RESULTS UPDATE -->
    <v-overlay
      absolute
      :value="isUpdateQuoteShow && $vuetify.breakpoint.lgAndUp"
    />

    <!-- HEADER -->
    <site-header
      ref="header"
    />

    <!-- SMALL DEVICES ADJUST OPTIONS -->
    <v-sheet
      v-if="$vuetify.breakpoint.mdAndDown"
      color="navy-medium"
      dark
      tile
      class="px-3 d-flex align-center control-wrapper"
    >
      <v-col>
        <v-btn
          v-if="!isQuotesLoading"
          outlined
          rounded
          small
          color="white"
          data-test="adjust_your_cover"
          @click="isOptionsDrawerVisible = !isOptionsDrawerVisible"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-tune
          </v-icon>
          Adjust your cover
        </v-btn>
      </v-col>
    </v-sheet>

    <!-- RESULTS TABLE / RESULTS FILTERS -->
    <v-container
      v-if="hasSucceededQuotes || !isQuotesLoading"
      :fluid="$vuetify.breakpoint.lgAndDown"
      class="py-4 px-md-8"
    >
      <v-row class="my-0">
        <!-- RESULTS FILTERS -->
        <v-col
          cols="12"
          lg="3"
          class="py-0"
        >
          <v-navigation-drawer
            v-model="isOptionsDrawerVisible"
            :app="$vuetify.breakpoint.mdAndDown"
            :right="$vuetify.breakpoint.mdAndDown"
            align-self="end"
            clipped
            floating
            touchless
            :color="$vuetify.breakpoint.lgAndUp ? 'transparent' : undefined"
            :width="$vuetify.breakpoint.lgAndUp ? 400 : 360"
            :height="$vuetify.breakpoint.lgAndUp ? 'auto' : '100%'"
            :permanent="$vuetify.breakpoint.lgAndUp"
            :style="$vuetify.breakpoint.lgAndUp && `position:sticky; top: ${fixedTopIndent}px;`"
            :class="[ $vuetify.breakpoint.lgAndUp && 'fix-height',
                      isQuotesLoading && 'v-navigation-drawer-disabled',
                      isUpdateQuoteShow && 'requote-shown' ]"
          >
            <results-options
              v-if="formData"
              :form-data="formData"
              :details-description="getInitDetailsDescription"
              :options="filterOptions"
              :selected-filters="selectedFilters"
              :is-update-quote-show="isUpdateQuoteShow"
              @click-edit-quote="goToForm"
              @click-close="isOptionsDrawerVisible = !isOptionsDrawerVisible"
              @click-details="openDetailsDrawer"
              @change-filter="handleChangeFilter"
            />
          </v-navigation-drawer>
        </v-col>

        <!-- RESULTS TABLE -->
        <v-col
          cols="12"
          lg="9"
          class="py-0 d-flex flex-column flex-grow-1"
        >
          <main
            class="flex-grow-1"
          >
            <results-table
              :quote-ref="quoteRef"
              :quotes-qty="quotesQuantity"
              :columns="resultsTableColumns"
              :insured-name="insuredName"
              :product-name="productName"
              :all-results-qty="allResults.length"
              :failed-quotes="failedResults"
              :is-no-results="isNoResults"
              :has-quote-summary="!!quoteSummary"
              :selected-optional-extras="selectedOptionalExtras"
              :fixed-top-indent="fixedTopIndent"
              :has-broker-results="hasBrokerResults"
              :is-broker-solution-message-shown="isBrokerSolutionMessageShown"
              :is-broker-solution-table-shown="isBrokerSolutionTableShown"
              :broker-quotes-qty="brokerShownResults.length"
              @open-drawer-details="openDetailsDrawer"
              @go-to-form="goToForm"
            >
              <!-- BROKER NOTIFICATIONS -->
              <template
                v-if="isBrokerSolutionMessageShown"
                #brokerResultsMessage
              >
                <span v-html="brokerResultsMessage" />
              </template>
              <!-- BROKER CUSTOM MESSAGE -->
              <template
                v-if="config.partnerMessage"
                #partnerMessage
              >
                <div v-html="config.partnerMessage" />
              </template>
              <!-- BROKER RESULTS -->
              <template
                v-if="isBrokerSolutionTableShown"
                #brokerResults
              >
                <div
                  v-for="(chosenQuote, index) of brokerShownResults"
                  :id="'chosen-quote-'+chosenQuote.id"
                  :key="'chosen-quote-'+chosenQuote.id"
                  class="quote-container mb-6"
                >
                  <transition name="fade-in">
                    <results-quote
                      v-show="chosenQuote.isVisible"
                      :quote="chosenQuote"
                      :is-view-monthly="isViewMonthly"
                      :details-description="getDetailsDescription"
                      :optional-extra-values="getOptionalExtrasValues"
                      @click-view-details="clickViewDetails($event, index)"
                      @open-drawer-details="openDetailsDrawer"
                      @toggle-quote-shown="toggleQuoteShown($event, brokerResults)"
                    />
                  </transition>
                </div>
              </template>
              <!-- QZ NOTIFICATION -->
              <template
                v-if="isQZShownMessageShown"
                #QZMessage
              >
                <results-broker-notification
                  v-if="isBrokerSolutionEnabled"
                  :has-broker-results="hasBrokerResults"
                  :quotes-qty="quotesQuantity"
                  :shown-results-cheapest-price="shownResultsCheapestPrice"
                />
                <span
                  v-else
                  v-html="QZMessage"
                />
              </template>

              <!-- ALL RESULTS -->
              <template
                v-if="isQZShown"
                #quotes
              >
                <div
                  v-for="(quote, index) of shownResults"
                  :key="quote.id"
                  class="quote-container mb-6"
                >
                  <transition name="fade-in">
                    <results-quote
                      v-show="quote.isVisible"
                      :quote="quote"
                      :is-view-monthly="isViewMonthly"
                      :details-description="getDetailsDescription"
                      :optional-extra-values="getOptionalExtrasValues"
                      @click-view-details="clickViewDetails($event, index)"
                      @open-drawer-details="openDetailsDrawer"
                      @toggle-quote-shown="toggleQuoteShown"
                    />
                  </transition>
                </div>
              </template>
            </results-table>
            <results-additional-quotes
              v-if="isAdditionalResultsCTAShown"
              @get-more-quotes="onBrokerSolutionModalSuccess"
            />
          </main>
        </v-col>
      </v-row>

      <!-- FOOTER -->
      <v-footer
        v-if="config.partnerFooter"
        padless
        color="transparent"
        tag="div"
      >
        <site-footer role="footer">
          <div v-html="config.partnerFooterText || ''" />
        </site-footer>
      </v-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />

      <!-- RESULTS FOUND SMALL MODAL -->
      <loading-notice
        :is-only-loading-shown="isBrokerSolutionEnabled"
        :is-loading="isQuotesLoading"
        :totalcount="quotesQuantity"
      />
    </v-container>

    <!-- ASIDE DRAWER -->
    <v-navigation-drawer
      v-model="isDetailsDrawerOpen"
      app
      right
      temporary
      touchless
      :width="drawerWidth"
      height="100%"
    >
      <keep-alive>
        <component
          :is="drawerComponent"
          ref="drawer"
          :quote="detailsQuote"
          :optional-extra-values="getOptionalExtrasValues"
          :quote-position-number="quotePositionNumber"
          :details-description="getDetailsDescription"
          :is-view-monthly="isViewMonthly"
          :is-details-drawer-open="isDetailsDrawerOpen"
          :selected-optional-extras="optionalExtras"
          :quote-summary="quoteSummary"
          :form-step-labels="formStepLabels"
          :product-name="productName"
          @open-drawer-details="openDetailsDrawer"
          @go-to-form="goToForm"
          @close-drawer="closeDetailsDrawer"
        />
      </keep-alive>
    </v-navigation-drawer>

    <!-- RESULTS FAILED MODAL -->
    <results-modal-alerts
      :modal-alert="modalAlert"
      @go-to-form="goToForm"
      @cancel="modalAlert = false"
    />
    <!-- BROKER SOLUTION EXIT ONLY MODAL -->
    <results-modal-alerts
      v-if="isBrokerSolutionModalShowed"
      ref="brokerModal"
      modal-alert="brokerModal"
      @success="onBrokerSolutionModalSuccess"
      @cancel="onBrokerSolutionModalCancel"
    />

    <!-- RESULTS LOADING MODAL -->
    <results-loading-modal
      :quoting-done-percentage="quotingDonePercentage"
      :has-succeeded-quotes="hasSucceededQuotes"
      :is-quotes-loading="isQuotesLoading"
    />
  </div>
</template>

<script>
import capitalize from 'lodash/capitalize';
import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneResult from '@/composables/useQuotezoneResult';
import useBrokerSolution from '@/composables/useBrokerSolution';

import SiteHeader from '@/components/SiteHeader/SiteHeader.vue';

import DetailsQuoteSummary from '@/components/car/Results/detailsDrawer/DetailsQuoteSummary.vue';
import ResultsTable from '@/components/car/Results/ResultsTable.vue';
import DetailsDescription from '@/components/car/Results/detailsDrawer/DetailsDescription.vue';
import ResultsModalAlerts from '@/components/car/Results/ResultsModalAlerts.vue';
import LoadingNotice from '@/components/car/Results/ResultsLoadingNotice.vue';
import ResultsAdditionalQuotes from '@/components/car/Results/ResultsAdditionalQuotes.vue';
import ResultsBrokerNotification from '@/components/car/Results/ResultsBrokerNotification';
import ResultsDetails from '@/components/breakdown/Results/ResultsDetails.vue';
import ResultsQuote from '@/components/breakdown/Results/ResultsQuote/ResultsQuote.vue';
import ResultsOptions from '@/components/breakdown/Results/ResultsOptions.vue';
import ResultsLoadingModal from '@/components/ResultsLoadingModal';

import {
  partnerFooters,
} from '@/helpers/common';
import { detailsDescription } from '@/helpers/products/breakdown';
import { inject } from '@vue/composition-api';
import ModalAlert from '@/components/car/Results/ModalAlert';

export default {
  name: 'page-breakdown-insurance-results',
  inject: [
    'config',
    'constants',
    'formMetadata',
  ],
  metaInfo () {
    return {
      title: `${this.config.partnerText} | ${this.formMetadata.productTitle} Insurance | Quotes`,
    };
  },
  components: {
    ...partnerFooters,
    SiteHeader,
    ResultsDetails,
    ResultsOptions,
    ResultsTable,
    DetailsQuoteSummary,
    DetailsDescription,
    ResultsModalAlerts,
    LoadingNotice,
    ResultsQuote,
    ModalAlert,
    ResultsLoadingModal,
    ResultsAdditionalQuotes,
    ResultsBrokerNotification,
  },
  data () {
    return {
      // Pass to DetailsList, based on data get step number
      // when click edit details
      formStepLabels: {
        'Your Cover': '1',
        'About You': '2',
      },
    };
  },
  computed: {
    message () {
      const coverLevel = this.selectedFilters.coverLevel || this.formData.cover.typeOfCover;
      return `prices for a <strong>${capitalize(coverLevel)}</strong>`;
    },
    QZMessage () {
      const name = this.insuredName ? `${this.insuredName}, we're` : "We're";

      return `${name} showing you ${this.message} breakdown policy.`;
    },
    brokerResultsMessage () {
      return `${this.config.partnerText} ${this.message} breakdown policy.`;
    },
    resultsTableColumns () {
      return [
        {
          name: this.isViewMonthly ? 'Annually' : 'Provider',
        },
        {
          name: this.isViewMonthly ? 'Monthly' : 'Annually',
        },
        {
          name: 'Cover Type',
        },
        {
          name: 'Local Cover',
        },
        {
          name: 'Nationwide Cover',
        },
        {
          name: 'Home Start',
        },
        {
          name: 'Onward Travel',
        },
        {
          name: 'European Cover',
        },
        {
          name: 'Policy Details',
        },
      ];
    },
    filterOptions () {
      return {
        optionalExtrasOptions: this.constants.OPTIONAL_EXTRAS_OPTIONS,
        coverLevelOptions: this.constants.COVER_LEVEL_OPTIONS,
      };
    },
    selectedFilters () {
      return {
        isViewMonthly: this.isViewMonthly,
        ...this.restSelectedFilters,
      };
    },
    getOptionalExtrasValues () {
      const allOptions = [this.constants.ROADSIDE_ASSISTANCE, ...this.filterOptions.optionalExtrasOptions];
      return allOptions.map(option => option.value);
    },
  },
  async created () {
    this.initSelectedFilters();
    await this.getQuotes(true);
    this.setDefaultSelectedFilters();
    if (this.isBrokerSolutionEnabled) {
      this.initExitOnlyModal();
    }
  },
  methods: {
    handleChangeFilter: function ({ filter, value }) {
      this.changeFilter({ filter, value }, this.selectedFilters);
    },
    setDefaultSelectedFilters () {
      if (!this?.formData?.cover) return;

      const filterCoverLevel = this.selectedFilters.coverLevel;
      const formCoverLevel = this.formData.cover.typeOfCover;
      const filterOptionalExtras = this.selectedFilters.optionalExtras;
      const optionalExtras = {
        homeStart: this.formData.cover.homeStart,
        altTravel: this.formData.cover.altTravel,
      };

      if (!filterCoverLevel || filterCoverLevel !== formCoverLevel) {
        this.handleChangeFilter({ filter: 'coverLevel', value: formCoverLevel });
      }

      if (!filterOptionalExtras?.length) {
        this.handleChangeFilter({
          filter: 'optionalExtras',
          value: Object.keys(optionalExtras).filter(extra => optionalExtras[extra]),
        });
      }
    },
  },
  setup (props, context) {
    const structure = inject('formMetadata');
    const stepIds = structure.steps.map(step => step.stepId);
    const common = useQuotezoneCommon(props, context);
    const result = useQuotezoneResult({
      stepIds,
      detailsDescription,
      gtmTracking: common.gtmTracking,
    }, context);
    const broker = useBrokerSolution(result, context);

    return {
      ...common,
      ...result,
      ...broker,
      capitalize,
    };
  },
};
</script>

<style lang="scss" src='@/assets/scss/results/core-component.scss'></style>
